// src/Dashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Thermometer,
  ClipboardList,
  RefreshCw,
  Menu as MenuIcon,
  BookOpen,
  GraduationCap,
  FileText,
  Beaker,
  Flame,
  ShieldCheck,
  PackageCheck,
  Bug,
  Wrench,
  Tag,
  ClipboardCheck,
  Brain,
  ShoppingBag,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import TopBar from "./components/TopBar";
import { canAccessShop } from "./features/shop/access";
import NormaWidget from "./features/norma/NormaWidget";
import AreeDiControllo from "./components/AreeDiControllo";
import AttivitaScadenzeWidgetContainer from "./components/AttivitaScadenzeWidgetContainer";
import DashboardRightModules from "./components/DashboardRightModules";
import StatusBanner from "./components/StatusBanner";
import MemoDocumenti from "./components/MemoDocumenti";
import Footer from "./components/Footer";
import { TrialPopupModal } from "./components/TrialPopupModal";

const fmtTime = (d) =>
  new Date(d).toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
  });

const TODAY_TASK_COLORS = {
  todo: "#2563eb",
  done: "#10b981",
  empty: "#cbd5e1",
};

function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseDateSafe(v) {
  if (!v) return null;
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
}

function toNumberOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function minutesDiffFromNow(dateValue) {
  const d = parseDateSafe(dateValue);
  if (!d) return null;
  return Math.round((Date.now() - d.getTime()) / 60000);
}

async function apiGetJson(url) {
  const res = await fetch(url, { credentials: "include" });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

async function apiPostJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

function mapTaskRowToDashboardItem(task) {
  const module = task.source_module || "";
  const typeByModule = {
    reminder_igiene_personale: "igiene_personale",
    reminder_temperature_frigo: "temp",
    reminder_sanificazione: "sanificazione",
    reminder_analisi_laboratorio: "analisi_laboratorio",
    reminder_formazione: "formazione",
    reminder_audit_haccp: "audit_haccp",
    reminder_audit_esterno: "audit_esterno",
    reminder_infestanti: "infestanti",
    reminder_manutenzione: "manutenzione",
    abbattimenti: "abbattimenti",
  };

  return {
    id: task.id,
    label: task.title,
    type: typeByModule[module] || "custom",
    date: task.target_date || task.due_date || ymd(new Date()),
    urgent: Number(task.priority) >= 4,
    warning: Number(task.priority) === 3,
    completedAt: task.completed_at ? new Date(task.completed_at).getTime() : null,
    raw: task,
  };
}

function isReminderTask(task) {
  const module = task?.source_module || "";
  return module === "abbattimenti" || module.startsWith("reminder_");
}

function abbattimentoRegistroUrl(task, complete = false) {
  const ref = task?.raw?.source_ref || task?.source_ref || null;
  const params = new URLSearchParams();
  if (ref) params.set("abbattimento_id", String(ref));
  if (complete) params.set("complete", "1");
  const query = params.toString();
  return query ? `/abbattimenti/registro?${query}` : "/abbattimenti/registro";
}

function taskActionUrl(task) {
  switch (task?.type) {
    case "igiene_personale":
      return "/igiene-personale";
    case "temp":
      return "/frighi";
    case "analisi_laboratorio":
      return "/api/documenti_azienda.php?cat=analisi_laboratorio";
    case "formazione":
      return "/api/documenti_azienda.php?cat=corso_alimentaristi";
    case "audit_esterno":
      return "/api/documenti_azienda.php?cat=audit_controllo_esterno";
    default:
      return null;
  }
}

function QuickActionCard({
  tone = "blue",
  icon,
  title,
  subtitle,
  disabled,
  onClick,
}) {
  const tones = {
    blue: "from-sky-400/30 to-sky-500/40 border-sky-200",
    green: "from-emerald-400/30 to-emerald-500/40 border-emerald-200",
    orange: "from-orange-400/30 to-orange-500/40 border-orange-200",
    purple: "from-violet-400/30 to-violet-500/40 border-violet-200",
    pink: "from-fuchsia-400/30 to-fuchsia-500/40 border-fuchsia-200",
    slate: "from-slate-300/40 to-slate-400/40 border-slate-200",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "w-full min-h-[110px] rounded-2xl border bg-gradient-to-br shadow-md text-left overflow-hidden",
        "transition hover:-translate-y-0.5 hover:shadow-lg",
        disabled ? "opacity-55 cursor-not-allowed hover:translate-y-0 hover:shadow-md" : "",
        tones[tone] || tones.slate,
      ].join(" ")}
    >
      <div className="h-full px-4 pt-4 pb-3 flex flex-col">
        <div className="flex items-start justify-between">
          <div className="h-10 w-10 rounded-2xl bg-white/70 border border-white/60 grid place-items-center">
            {icon}
          </div>
        </div>

        <div className="mt-3">
          <div className="text-[15px] font-semibold leading-tight text-slate-900">
            {title}
          </div>
          {subtitle ? (
            <div className="mt-1 text-[12px] leading-snug text-slate-700/80">
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>
    </button>
  );
}

export default function Dashboard({ onLogout }) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [done, setDone] = useState([]);
  const [status, setStatus] = useState({
    frighi: { level: "green", note: "OK" },
    haccp: { level: "green", note: "Documenti completi" },
    scadenze: { level: "green", note: "Nessun prodotto in scadenza" },
  });

  const [menuOpen, setMenuOpen] = useState(false);
  const [dismissedStatusKey, setDismissedStatusKey] = useState(
    () => window.localStorage.getItem("dashboard_status_dismissed") || ""
  );
  const [showDone, setShowDone] = useState(false);
  const [igieneToday, setIgieneToday] = useState({
    loading: true,
    compiled: false,
    esito: null,
  });

  const [userInfo, setUserInfo] = useState(null);
  const [profileDocsAlert, setProfileDocsAlert] = useState({
    loading: true,
    hasDocuments: true,
    count: 0,
    profileUrl: "/admin?tab=profilo",
    requiredPresent: 0,
    requiredTotal: 0,
    requiredMissing: 0,
    checklist: [],
  });
  const [documentDeadlines, setDocumentDeadlines] = useState([]);

  const [upgradeModal, setUpgradeModal] = useState({
    open: false,
    featureLabel: "",
  });

  const [kpi, setKpi] = useState({
    cryo: { active24h: 0, total: 0 },
    sanif: { pct: 0, done: 0, total: 0 },
    merci: { count: 0 },
    abbatt: { done: 0, total: 0 },
    etichette: { count: 0 },
    operatori: { label: "0" },
  });

  const [cryoDevices, setCryoDevices] = useState([]);
  const [selectedProbe, setSelectedProbe] = useState(null);
  const [liveHistory, setLiveHistory] = useState([]);
  const [loadingLive, setLoadingLive] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/me.php", { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setUserInfo(data);
        }
      } catch {
        // non blocchiamo
      }
    })();
  }, []);

  async function loadProfileDocumentsStatus() {
    try {
      const json = await apiGetJson("/api/azienda_profile_status.php");
      const docs = json?.documenti_amministrativi || {};
      setProfileDocsAlert({
        loading: false,
        hasDocuments: !!docs.has_documents,
        count: Number(docs.count || 0),
        profileUrl: docs.profile_url || "/admin?tab=profilo",
        requiredPresent: Number(docs.required_present || 0),
        requiredTotal: Number(docs.required_total || 0),
        requiredMissing: Number(docs.required_missing || 0),
        checklist: Array.isArray(docs.checklist) ? docs.checklist : [],
      });

      setStatus((prev) => ({
        ...prev,
        haccp: Number(docs.required_missing || 0) === 0
          ? { level: "green", note: `Documenti amministrativi completi (${Number(docs.required_present || 0)}/${Number(docs.required_total || 0)})` }
          : { level: "yellow", note: `Documenti amministrativi mancanti (${Number(docs.required_missing || 0)} da caricare)` },
      }));

      return docs;
    } catch (err) {
      console.error("azienda_profile_status:", err);
      setProfileDocsAlert({
        loading: false,
        hasDocuments: true,
        count: 0,
        profileUrl: "/admin?tab=profilo",
        requiredPresent: 0,
        requiredTotal: 0,
        requiredMissing: 0,
        checklist: [],
      });
      return null;
    }
  }

  const perms = useMemo(() => {
    if (!userInfo) return [];
    if (Array.isArray(userInfo.permessi)) return userInfo.permessi;
    if (Array.isArray(userInfo.permissions)) return userInfo.permissions;
    if (Array.isArray(userInfo.user?.permessi)) return userInfo.user.permessi;
    return [];
  }, [userInfo]);

  const hasPerm = (code) => {
    if (!code) return true;
    if (!userInfo) return false;
    if (userInfo.ruolo === "superadmin") return true;
    if (!Array.isArray(perms)) return false;
    if (perms.includes("ALL")) return true;
    return perms.includes(code);
  };

  const hasAnyPerm = (codes) => {
    if (!Array.isArray(codes)) return hasPerm(codes);
    return codes.some((code) => hasPerm(code));
  };

  const openUpgradeModal = (label) =>
    setUpgradeModal({ open: true, featureLabel: label });

  const closeUpgradeModal = () =>
    setUpgradeModal({ open: false, featureLabel: "" });

  async function loadDashboardKpi() {
    try {
      const res = await fetch("/api/dashboard_aree_controllo.php", {
        credentials: "include",
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.success) {
        throw new Error(json?.error || "Errore caricamento KPI dashboard");
      }

      const d = json.data || {};

      setKpi({
        cryo: {
          active24h: Number(d?.cryo?.active24h || 0),
          total: Number(d?.cryo?.total || 0),
        },
        sanif: {
          pct: Number(d?.sanif?.pct || 0),
          done: Number(d?.sanif?.done || 0),
          total: Number(d?.sanif?.total || 0),
        },
        merci: {
          count: Number(d?.merci?.count || 0),
        },
        abbatt: {
          done: Number(d?.abbatt?.done || 0),
          total: Number(d?.abbatt?.total || 0),
        },
        etichette: {
          count: Number(d?.etichette?.count || 0),
        },
        operatori: {
          label: String(d?.operatori?.label ?? "0"),
        },
      });
    } catch (err) {
      console.error("dashboard_aree_controllo:", err);
      setKpi({
        cryo: { active24h: 0, total: 0 },
        sanif: { pct: 0, done: 0, total: 0 },
        merci: { count: 0 },
        abbatt: { done: 0, total: 0 },
        etichette: { count: 0 },
        operatori: { label: "0" },
      });
    }
  }

  async function loadIgieneToday() {
    try {
      const res = await fetch(
        "/api/checklist_igiene_personale_today.php?reparto=Cucina",
        { credentials: "include" }
      );
      const js = await res.json();

      if (!res.ok || !js?.success) {
        throw new Error(js?.error || "Errore stato igiene personale");
      }

      setIgieneToday({
        loading: false,
        compiled: !!js.compiled,
        esito: js.esito || null,
      });

      setStatus((prev) => ({
        ...prev,
        igiene: js.compiled
          ? js.esito === "NON_CONFORME"
            ? { level: "yellow", note: "Checklist igiene personale: NON conforme (questa settimana)" }
            : { level: "green", note: "Checklist igiene personale: conforme (questa settimana)" }
          : { level: "yellow", note: "Checklist igiene personale: MANCANTE (questa settimana)" },
      }));

      return js;
    } catch {
      setIgieneToday({ loading: false, compiled: false, esito: null });
      setStatus((prev) => ({
        ...prev,
        igiene: { level: "yellow", note: "Checklist igiene personale: stato non disponibile" },
      }));
      return null;
    }
  }

  async function loadTodayTasks() {
    const today = ymd(new Date());

    await apiGetJson(`/api/tasks/reminders_sync.php?_=${Date.now()}`);

    const [openRes, doneRes] = await Promise.all([
      apiGetJson(`/api/tasks/list.php?to=${today}&status=open&date_field=target`),
      apiGetJson(`/api/tasks/list.php?from=${today}&to=${today}&status=done&date_field=completed`),
    ]);

    return {
      open: (openRes.tasks || []).filter(isReminderTask).map(mapTaskRowToDashboardItem),
      done: (doneRes.tasks || []).filter(isReminderTask).map(mapTaskRowToDashboardItem),
    };
  }

  function normalizeCryoListResponse(json) {
    const rows = json?.items || json?.devices || json?.data || json?.rows || [];
    if (!Array.isArray(rows)) return [];

    return rows.map((r, idx) => {
      const mac =
        r.mac_address ||
        r.mac ||
        r.device_mac ||
        r.probe_mac ||
        `device_${idx}`;

      const id = r.dispositivo_id || r.id || r.device_id || null;
      const nome =
        r.nome ||
        r.frigo_nome ||
        r.label ||
        r.display_name ||
        r.nome_dispositivo ||
        mac;

      const tempUltima = toNumberOrNull(
        r.temp_ultima ?? r.temperatura ?? r.temp ?? r.last_temp ?? null
      );

      const min = toNumberOrNull(r.limite_min ?? r.min ?? r.min_temp ?? null);
      const max = toNumberOrNull(r.limite_max ?? r.max ?? r.max_temp ?? null);
      const lastSeen =
        r.last_seen ||
        r.ultima_lettura ||
        r.rilevata_il ||
        r.updated_at ||
        r.created_at ||
        null;

      const minutesAgo = minutesDiffFromNow(lastSeen);
      const stale = minutesAgo === null ? true : minutesAgo > 120;

      const outOfRange =
        tempUltima !== null &&
        ((min !== null && tempUltima < min) || (max !== null && tempUltima > max));

      return {
        key: String(mac),
        mac_address: mac,
        id,
        nome,
        temp_ultima: tempUltima,
        limite_min: min,
        limite_max: max,
        last_seen: lastSeen,
        stale,
        outOfRange,
      };
    });
  }

  function computeFrighiStatus(devices) {
    if (!devices.length) {
      return {
        level: "yellow",
        note: "Nessun Cryo configurato",
      };
    }

    const offline = devices.filter((d) => d.stale).length;
    const out = devices.filter((d) => d.outOfRange).length;
    const fresh = devices.length - offline;

    if (out > 0) {
      return {
        level: "red",
        note: `${out} frigorifero/i fuori limite su ${devices.length}`,
      };
    }

    if (offline === devices.length) {
      return {
        level: "yellow",
        note: `Nessun dispositivo aggiornato di recente`,
      };
    }

    if (offline > 0) {
      return {
        level: "yellow",
        note: `${fresh}/${devices.length} dispositivi aggiornati, ${offline} non recenti`,
      };
    }

    return {
      level: "green",
      note: `Tutti nei limiti (${devices.length} dispositivi)`,
    };
  }

  async function loadCryoDevices() {
    try {
      const json = await apiGetJson(`/api/api_cryo_list.php?_=${Date.now()}`);
      const devices = normalizeCryoListResponse(json);

      setCryoDevices(devices);
      setStatus((prev) => ({
        ...prev,
        frighi: computeFrighiStatus(devices),
      }));

      setSelectedProbe((prev) => {
        if (prev && devices.some((d) => d.key === prev)) return prev;
        return devices[0]?.key || null;
      });

      return devices;
    } catch (err) {
      console.error("api_cryo_list:", err);
      setCryoDevices([]);
      setSelectedProbe(null);
      setLiveHistory([]);
      setStatus((prev) => ({
        ...prev,
        frighi: { level: "yellow", note: "Stato Cryo non disponibile" },
      }));
      return [];
    }
  }

  function normalizeCryoHistoryResponse(json) {
    const rows = json?.items || json?.history || json?.rows || json?.data || [];
    if (!Array.isArray(rows)) return [];

    return rows
      .map((r) => {
        const rawTs =
          r.rilevata_il ||
          r.created_at ||
          r.ts ||
          r.timestamp ||
          r.data_ora ||
          r.recorded_at ||
          null;

        const d = parseDateSafe(rawTs);
        const value = toNumberOrNull(
          r.temperatura ?? r.temp ?? r.value ?? r.valore ?? r.temp_ultima ?? null
        );

        if (!d || value === null) return null;

        return {
          tsRaw: d.getTime(),
          ts: fmtTime(d),
          value,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.tsRaw - b.tsRaw);
  }

  async function loadCryoHistoryByDevice(device) {
    if (!device) {
      setLiveHistory([]);
      return [];
    }

    setLoadingLive(true);

    const tries = [
      `/api/api_cryo_history.php?mac_address=${encodeURIComponent(device.mac_address)}&limit=30`,
      `/api/api_cryo_history.php?mac=${encodeURIComponent(device.mac_address)}&limit=30`,
      device.id
        ? `/api/api_cryo_history.php?dispositivo_id=${encodeURIComponent(device.id)}&limit=30`
        : null,
      device.id
        ? `/api/api_cryo_history.php?id=${encodeURIComponent(device.id)}&limit=30`
        : null,
    ].filter(Boolean);

    for (const url of tries) {
      try {
        const json = await apiGetJson(url);
        const history = normalizeCryoHistoryResponse(json);

        if (history.length > 0) {
          setLiveHistory(history);
          setLoadingLive(false);
          return history;
        }
      } catch {
        // provo la variante successiva
      }
    }

    setLiveHistory([]);
    setLoadingLive(false);
    return [];
  }

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const [, , todayTasks, devices, profileDocs, deadlineData] = await Promise.all([
          loadIgieneToday(),
          loadDashboardKpi(),
          loadTodayTasks(),
          loadCryoDevices(),
          loadProfileDocumentsStatus(),
          apiGetJson("/api/memo_scadenze.php").catch(() => ({ items: [] })),
        ]);

        const s = {
          haccp: Number(profileDocs?.required_missing || 0) === 0
            ? { level: "green", note: `Documenti amministrativi completi (${Number(profileDocs?.required_present || 0)}/${Number(profileDocs?.required_total || 0)})` }
            : { level: "yellow", note: `Documenti amministrativi mancanti (${Number(profileDocs?.required_missing || 0)} da caricare)` },
          scadenze: { level: "yellow", note: "3 prodotti in scadenza entro 48h" },
        };

        if (!cancelled) {
          setTasks(todayTasks?.open || []);
          setDone(todayTasks?.done || []);
          setDocumentDeadlines(Array.isArray(deadlineData?.items) ? deadlineData.items : []);
          setStatus((prev) => ({
            ...prev,
            ...s,
            frighi: computeFrighiStatus(devices || []),
            igiene: prev.igiene,
          }));
          setLoading(false);
        }
      } catch (err) {
        console.error("Errore bootstrap dashboard:", err);

        if (!cancelled) {
          setTasks([]);
          setDone([]);
          setCryoDevices([]);
          setSelectedProbe(null);
          setLiveHistory([]);
          setLoading(false);
        }
      }
    }

    bootstrap();

    const onFocus = () => bootstrap();
    window.addEventListener("focus", onFocus);

    const interval = setInterval(() => {
      bootstrap();
    }, 60000);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const device = cryoDevices.find((d) => d.key === selectedProbe) || null;
    loadCryoHistoryByDevice(device);
  }, [selectedProbe, cryoDevices]);

  const missingProfileDocuments = useMemo(() => {
    return (profileDocsAlert.checklist || []).filter((item) => item.required && !item.present);
  }, [profileDocsAlert.checklist]);

  const probes = useMemo(() => {
    return cryoDevices.map((d) => ({
      key: d.key,
      label: d.nome || d.mac_address,
      temp_ultima: d.temp_ultima,
      stale: d.stale,
      outOfRange: d.outOfRange,
    }));
  }, [cryoDevices]);

  const dataForSelected = useMemo(() => liveHistory, [liveHistory]);

  const selectedProbeMeta = useMemo(() => {
    return cryoDevices.find((d) => d.key === selectedProbe) || null;
  }, [cryoDevices, selectedProbe]);

  function handleOpen(t) {
    switch (t.type) {
      case "temp":
        if (hasPerm("CRYO_VIEW")) navigate(taskActionUrl(t));
        else openUpgradeModal("Controllo temperature");
        break;
      case "abbattimenti":
        navigate(abbattimentoRegistroUrl(t));
        break;
      case "pulizia":
        if (hasPerm("PIANO_PULIZIA")) window.location.href = "/piano-pulizia.php";
        else openUpgradeModal("Piano Pulizia");
        break;
      case "sanificazione":
        if (hasPerm("PIANO_SANIF")) navigate("/sanificazione");
        else openUpgradeModal("Piano di Sanificazione");
        break;
      case "ricevimento":
        if (hasPerm("RICEVIMENTO_MERCI")) navigate("/ricevimento");
        else openUpgradeModal("Ricevimento Merci");
        break;
      case "olio":
        if (hasPerm("OLII_FRITTURA")) navigate("/verifica-olii-frittura");
        else openUpgradeModal("Verifica Olii Frittura");
        break;
      case "igiene_personale":
        if (hasPerm("IGIENE_PERSONALE")) navigate(taskActionUrl(t));
        else openUpgradeModal("Checklist Igiene Personale");
        break;
      case "analisi_laboratorio":
        window.location.href = taskActionUrl(t);
        break;
      case "formazione":
        window.location.href = "/api/documenti_azienda.php?cat=corso_alimentaristi";
        break;
      case "audit_haccp":
        navigate("/audit-haccp");
        break;
      case "audit_esterno":
        window.location.href = taskActionUrl(t);
        break;
      case "infestanti":
        if (infestantiHref) window.location.href = infestantiHref;
        break;
      case "manutenzione":
        navigate("/manutenzioni");
        break;
      default:
        navigate("/");
    }
  }

  async function handleComplete(t) {
    if (t.type === "abbattimenti") {
      navigate(abbattimentoRegistroUrl(t, true));
      return;
    }

    const actionUrl = taskActionUrl(t);
    if (actionUrl) {
      if (actionUrl.startsWith("/api/")) window.location.href = actionUrl;
      else navigate(actionUrl);
      return;
    }

    try {
      await apiPostJson("/api/tasks/complete.php", { id: t.id });
      setTasks((prev) => prev.filter((x) => x.id !== t.id));
      setDone((prev) => [{ ...t, completedAt: Date.now() }, ...prev]);
    } catch (err) {
      window.alert(err.message || "Impossibile completare l'attività");
    }
  }

  async function restoreTask(t) {
    try {
      await apiPostJson("/api/tasks/restore.php", { id: t.id });
      setDone((prev) => prev.filter((x) => x.id !== t.id));
      setTasks((prev) => [{ ...t, completedAt: null }, ...prev]);
    } catch (err) {
      window.alert(err.message || "Impossibile ripristinare l'attività");
    }
  }

  const todayTaskItems = useMemo(() => {
    const today = ymd(new Date());
    return tasks.filter((t) => (t.date || today) === today);
  }, [tasks]);

  const todayTaskOverview = useMemo(() => {
    const todoCount = todayTaskItems.length;
    const doneCount = done.length;
    const total = todoCount + doneCount;
    const completionPct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

    const chartData =
      total > 0
        ? [
          { name: "Da fare", value: todoCount, color: TODAY_TASK_COLORS.todo },
          { name: "Completate", value: doneCount, color: TODAY_TASK_COLORS.done },
        ].filter((item) => item.value > 0)
        : [{ name: "Nessuna attivita", value: 1, color: TODAY_TASK_COLORS.empty }];

    return {
      todoCount,
      doneCount,
      total,
      completionPct,
      chartData,
    };
  }, [todayTaskItems, done]);

  const urgentCount = todayTaskItems.filter((t) => t.urgent).length;
  const fridgeOutOfRangeCount = cryoDevices.filter((device) => device.outOfRange).length;
  const fridgeOfflineCount = cryoDevices.filter((device) => device.stale).length;
  const hasCriticalStatus = fridgeOutOfRangeCount > 0;
  const hasWarningStatus = fridgeOfflineCount > 0;
  const statusBannerTone =
    urgentCount > 0 || hasCriticalStatus
      ? "bad"
      : todayTaskOverview.todoCount > 0 || hasWarningStatus
        ? "warn"
        : "ok";
  const statusBannerTitle =
    statusBannerTone === "bad"
      ? "Intervento richiesto"
      : statusBannerTone === "warn"
        ? "Attivita da completare"
        : "Tutto sotto controllo";
  const statusBannerSubtitle =
    statusBannerTone === "ok"
      ? "Frigoriferi nei limiti e nessuna attività da svolgere oggi"
      : [
          fridgeOutOfRangeCount > 0
            ? `${fridgeOutOfRangeCount} frigorifer${fridgeOutOfRangeCount === 1 ? "o" : "i"} fuori limite`
            : null,
          fridgeOfflineCount > 0
            ? `${fridgeOfflineCount} dispositiv${fridgeOfflineCount === 1 ? "o frigo non aggiornato" : "i frigo non aggiornati"}`
            : null,
          todayTaskOverview.todoCount > 0
            ? `${todayTaskOverview.todoCount} attivit${todayTaskOverview.todoCount === 1 ? "à" : "à"} da svolgere oggi`
            : null,
          urgentCount > 0
            ? `${urgentCount} urgent${urgentCount === 1 ? "e" : "i"}`
            : null,
        ].filter(Boolean).join(" · ");
  const statusAlertKey = [
    ymd(new Date()),
    fridgeOutOfRangeCount,
    fridgeOfflineCount,
    todayTaskOverview.todoCount,
    urgentCount,
  ].join(":");
  const statusAlertDismissed =
    statusBannerTone !== "ok" && dismissedStatusKey === statusAlertKey;

  function dismissStatusAlert() {
    window.localStorage.setItem("dashboard_status_dismissed", statusAlertKey);
    setDismissedStatusKey(statusAlertKey);
  }

  function restoreStatusAlert() {
    window.localStorage.removeItem("dashboard_status_dismissed");
    setDismissedStatusKey("");
  }

  // Il monitoraggio completo e il caricamento del verbale sono disponibili
  // sia per la gestione interna sia quando opera una ditta esterna.
  const infestantiHref = "/infestanti";
  const infestantiLabel = "Monitoraggio Infestanti";

  const isAdmin = userInfo?.ruolo === "admin" || userInfo?.ruolo === "superadmin";
  const isUser = userInfo?.ruolo === "utente";

  const aziendaLabel =
    userInfo?.ragione_sociale ||
    userInfo?.azienda_nome ||
    null;

  const userLabel = userInfo?.nome || userInfo?.ruolo || "Utente";
  const userEmail = userInfo?.email || "";

  const canCryoView = hasPerm("CRYO_VIEW");
  const canSanif = hasPerm("PIANO_SANIF");
  const canRicev = hasPerm("RICEVIMENTO_MERCI");
  const canEtichette = hasPerm("ETICHETTE");
  const canIgiene = hasPerm("IGIENE_PERSONALE");

  return (
    <>
      <TopBar
        title="Dashboard"
        onLogout={onLogout}
        user={{
          name: userLabel,
          email: userEmail,
        }}
      />

      <div className="min-h-screen bg-slate-100 p-4 md:p-6">
        <header className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold">Dashboard</h1>
            <span className="text-slate-600">Panoramica giornaliera e priorità</span>
            {aziendaLabel ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-200/60 px-3 py-1 text-xs text-slate-700">
                {aziendaLabel}
                {userInfo?.ruolo === "superadmin" && (
                  <span className="bg-rose-500 text-white px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide">
                    superadmin
                  </span>
                )}
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {urgentCount > 0 && (
              <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 text-amber-700 px-3 py-1 text-sm">
                <AlertTriangle size={14} />
                {urgentCount} urgenti
              </span>
            )}
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 bg-white hover:bg-slate-50"
            >
              <RefreshCw size={16} /> Aggiorna
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-blue-600 text-white hover:bg-blue-700"
              aria-label="Apri menu"
            >
              <MenuIcon size={18} /> Menu
            </button>
          </div>
        </header>

        <div className="mb-8">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-stretch">
            <div className="xl:col-span-2 h-full">
              {statusAlertDismissed ? (
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-slate-700">Avviso azzerato</div>
                    <div className="mt-1 text-xs text-slate-500">
                      Ricomparirà automaticamente se cambiano attività o stato dei frigoriferi.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={restoreStatusAlert}
                    className="rounded-xl border px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Ripristina
                  </button>
                </div>
              ) : (
                <StatusBanner
                  tone={statusBannerTone}
                  title={statusBannerTitle}
                  subtitle={statusBannerSubtitle}
                  time={new Date().toLocaleTimeString("it-IT", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  onDismiss={statusBannerTone !== "ok" ? dismissStatusAlert : undefined}
                />
              )}
            </div>

            <div className="xl:col-span-1 h-full">
              <MemoDocumenti
                items={[
                  ...(!profileDocsAlert.loading && !profileDocsAlert.hasDocuments
                    ? [{
                        color: "red",
                        text: "Carica i documenti amministrativi / autorizzativi nel profilo azienda",
                        onClick: () => navigate("/admin?tab=profilo"),
                      }]
                    : []),
                  ...(!profileDocsAlert.loading && profileDocsAlert.requiredMissing > 0
                    ? missingProfileDocuments.slice(0, 2).map((item) => ({
                        color: "red",
                        text: `Manca: ${item.label}`,
                        onClick: () => navigate("/admin?tab=profilo"),
                      }))
                    : []),
                  ...documentDeadlines
                    .filter((item) => item.key === "tamponi" || item.key === "formazione")
                    .map((item) => ({
                      color: item.color || "slate",
                      text: Number(item.days) < 0
                        ? `Scaduta da ${Math.abs(Number(item.days))} giorni: ${item.label}`
                        : `${Number(item.days)} giorni per ${item.label}`,
                      onClick: () => {
                        window.location.href = item.url;
                      },
                    })),
                  {
                    color: "slate",
                    text: profileDocsAlert.requiredTotal > 0
                      ? `Checklist profilo: ${profileDocsAlert.requiredPresent}/${profileDocsAlert.requiredTotal} obbligatori presenti`
                      : "Verifica profilo azienda e archivio documenti",
                    onClick: () => navigate("/admin?tab=profilo"),
                  },
                ]}
              />
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="mb-8">
            <AreeDiControllo
              data={kpi}
              onOpen={(key) => {
                if (key === "temp") {
                  if (canCryoView) navigate("/frighi");
                  else openUpgradeModal("Controllo temperature");
                }
                if (key === "sanificazione") {
                  if (canSanif) navigate("/sanificazione");
                  else openUpgradeModal("Piano di Sanificazione");
                }
                if (key === "ricevimento") {
                  if (canRicev) navigate("/ricevimento");
                  else openUpgradeModal("Ricevimento Merci");
                }
                if (key === "abbattimenti") navigate("/abbattimenti");
                if (key === "etichette") {
                  if (canEtichette) navigate("/etichette");
                  else openUpgradeModal("Etichette");
                }
                if (key === "igiene") {
                  if (canIgiene) navigate("/igiene-personale");
                  else openUpgradeModal("Checklist Igiene Personale");
                }
              }}
            />
          </div>
        )}

        {isUser && (
          <div className="mb-8">
            <div className="mb-3">
              <div className="text-xl font-semibold text-slate-800">Azioni rapide</div>
              <div className="text-sm text-slate-600">
                Accesso veloce ai moduli principali
              </div>
            </div>

            <div className="w-full">
              <div
                className={[
                  "grid grid-cols-1 gap-4",
                  "sm:grid-cols-2",
                  "lg:[grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]",
                  "2xl:[grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]",
                ].join(" ")}
              >
                <QuickActionCard
                  tone="blue"
                  icon={<Thermometer size={18} />}
                  title="Controllo Temperatura"
                  disabled={!canCryoView}
                  onClick={() =>
                    canCryoView ? navigate("/frighi") : openUpgradeModal("Controllo temperature")
                  }
                />

                <QuickActionCard
                  tone="green"
                  icon={<ShieldCheck size={18} />}
                  title="Sanificazione"
                  disabled={!canSanif}
                  onClick={() =>
                    canSanif ? navigate("/sanificazione") : openUpgradeModal("Piano di Sanificazione")
                  }
                />

                <QuickActionCard
                  tone="orange"
                  icon={<PackageCheck size={18} />}
                  title="Ricezione Merci"
                  disabled={!canRicev}
                  onClick={() =>
                    canRicev ? navigate("/ricevimento") : openUpgradeModal("Ricevimento Merci")
                  }
                />

                <QuickActionCard
                  tone="purple"
                  icon={<ClipboardList size={18} />}
                  title="Abbattimenti"
                  onClick={() => navigate("/abbattimenti")}
                />

                <QuickActionCard
                  tone="pink"
                  icon={<Tag size={18} />}
                  title="Etichette"
                  disabled={!canEtichette}
                  onClick={() =>
                    canEtichette ? navigate("/etichette") : openUpgradeModal("Etichette")
                  }
                />

                <QuickActionCard
                  tone="slate"
                  icon={<ClipboardCheck size={18} />}
                  title="Igiene Personale"
                  disabled={!canIgiene}
                  onClick={() =>
                    canIgiene
                      ? navigate("/igiene-personale")
                      : openUpgradeModal("Checklist Igiene Personale")
                  }
                />
              </div>
            </div>
          </div>
        )}

        <div className="mb-8">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2">
              <AttivitaScadenzeWidgetContainer />
            </div>
            <div className="xl:col-span-1">
              <DashboardRightModules />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="xl:col-span-1"
          >
            <div className="rounded-2xl shadow-sm bg-white">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <ClipboardList size={18} /> Cose da fare oggi
                  </h2>
                  <button
                    onClick={() => setShowDone((v) => !v)}
                    className="text-sm underline hover:opacity-80"
                  >
                    {showDone ? "Nascondi dettagli" : `Mostra dettagli (${todayTaskOverview.total})`}
                  </button>
                </div>

                <div className="mb-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] gap-4 items-center">
                    <div className="h-[130px] relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={todayTaskOverview.chartData}
                            dataKey="value"
                            innerRadius={38}
                            outerRadius={58}
                            paddingAngle={todayTaskOverview.total > 0 ? 3 : 0}
                            stroke="none"
                          >
                            {todayTaskOverview.chartData.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name) =>
                              todayTaskOverview.total > 0
                                ? [value, name]
                                : [0, "Attivita"]
                            }
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 grid place-items-center pointer-events-none">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-slate-900">
                            {todayTaskOverview.completionPct}%
                          </div>
                          <div className="text-[11px] font-semibold uppercase text-slate-500">
                            Fatto
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">
                        Panoramica di oggi
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        <div className="rounded-xl bg-white border border-slate-200 p-3">
                          <div className="text-2xl font-bold text-slate-900">
                            {todayTaskOverview.total}
                          </div>
                          <div className="text-xs text-slate-500">Totali</div>
                        </div>
                        <div className="rounded-xl bg-white border border-slate-200 p-3">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: TODAY_TASK_COLORS.todo }}
                            />
                            <div className="text-2xl font-bold text-slate-900">
                              {todayTaskOverview.todoCount}
                            </div>
                          </div>
                          <div className="text-xs text-slate-500">Da fare</div>
                        </div>
                        <div className="rounded-xl bg-white border border-slate-200 p-3">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: TODAY_TASK_COLORS.done }}
                            />
                            <div className="text-2xl font-bold text-slate-900">
                              {todayTaskOverview.doneCount}
                            </div>
                          </div>
                          <div className="text-xs text-slate-500">Completate</div>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-slate-500">
                        Include le attivita aperte fino a oggi e quelle completate oggi.
                      </div>
                    </div>
                  </div>
                </div>

                {showDone && (loading ? (
                  <div className="text-slate-500">Caricamento…</div>
                ) : (
                  <ul className="space-y-2">
                    {todayTaskItems.map((t) => (
                      <li
                        key={t.id}
                        className="bg-white rounded-xl p-3 flex items-start justify-between border border-slate-200"
                      >
                        <div className="pr-3">
                          <p className="font-medium leading-snug">{t.label}</p>
                          <div className="mt-1 flex gap-2">
                            {t.urgent && (
                              <span className="text-xs rounded-md px-2 py-0.5 bg-rose-100 text-rose-700">
                                Urgente
                              </span>
                            )}
                            {t.warning && (
                              <span className="text-xs rounded-md px-2 py-0.5 bg-slate-100 text-slate-700">
                                Da programmare
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpen(t)}
                            className="px-3 py-1.5 rounded-lg border bg-white hover:bg-slate-50"
                          >
                            Apri
                          </button>
                          <button
                            onClick={() => handleComplete(t)}
                            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Completa
                          </button>
                        </div>
                      </li>
                    ))}
                    {todayTaskItems.length === 0 && (
                      <li className="text-slate-500 italic">Nessuna attività pendente</li>
                    )}
                    </ul>
                ))}

                <AnimatePresence initial={false}>
                  {showDone && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-4 border-t pt-3"
                    >
                      <h3 className="text-sm font-semibold mb-2">Completate</h3>
                      <ul className="space-y-2">
                        {done.map((t) => (
                          <li
                            key={t.id}
                            className="bg-slate-50 rounded-xl p-3 flex items-start justify-between border border-slate-200"
                          >
                            <div className="pr-3">
                              <p className="font-medium leading-snug line-through">{t.label}</p>
                              <div className="text-xs text-slate-500">
                                completata alle {fmtTime(t.completedAt)}
                              </div>
                            </div>
                            <button
                              onClick={() => restoreTask(t)}
                              className="px-3 py-1.5 rounded-lg border bg-white hover:bg-slate-50"
                            >
                              Ripristina
                            </button>
                          </li>
                        ))}
                        {done.length === 0 && (
                          <li className="text-slate-500 italic">Nessuna attività completata</li>
                        )}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="xl:col-span-1"
          >
            <div className="rounded-2xl shadow-sm bg-white h-full">
              <div className="p-5">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Thermometer size={18} /> Stato Attuale
                </h2>
                <div className="space-y-3">
                  <Semaforo label="Temperatura Frigoriferi" state={status.frighi} />
                  <Semaforo label="Documenti HACCP" state={status.haccp} />
                  <Semaforo label="Scadenze Prossime" state={status.scadenze} />
                  <Semaforo label="Igiene Personale" state={status.igiene} />
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="xl:col-span-1"
          >
            <div className="rounded-2xl shadow-sm bg-white h-full">
              <div className="p-5">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <h2 className="text-lg font-semibold">Temperatura Live</h2>
                  <div className="flex gap-2 flex-wrap">
                    {probes.map((p) => (
                      <button
                        key={p.key}
                        onClick={() => setSelectedProbe(p.key)}
                        className={`px-3 py-1.5 rounded-lg border ${p.key === selectedProbe
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white hover:bg-slate-50"
                          }`}
                        title={
                          p.temp_ultima !== null
                            ? `${p.label}: ${p.temp_ultima.toFixed(1)} °C`
                            : p.label
                        }
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedProbeMeta ? (
                  <div className="mb-3 text-sm text-slate-600">
                    <div className="font-medium text-slate-800">
                      {selectedProbeMeta.nome}
                    </div>
                    <div>
                      Ultima lettura:{" "}
                      {selectedProbeMeta.temp_ultima !== null
                        ? `${selectedProbeMeta.temp_ultima.toFixed(1)} °C`
                        : "—"}
                    </div>
                    <div>
                      Limiti:{" "}
                      {selectedProbeMeta.limite_min !== null || selectedProbeMeta.limite_max !== null
                        ? `${selectedProbeMeta.limite_min ?? "—"} / ${selectedProbeMeta.limite_max ?? "—"} °C`
                        : "non impostati"}
                    </div>
                    <div>
                      Ultimo aggiornamento:{" "}
                      {selectedProbeMeta.last_seen
                        ? String(selectedProbeMeta.last_seen)
                        : "non disponibile"}
                    </div>
                  </div>
                ) : null}

                <div className="h-56">
                  {loadingLive ? (
                    <div className="text-slate-500">Caricamento storico…</div>
                  ) : dataForSelected.length === 0 ? (
                    <div className="text-slate-500">Nessun dato disponibile…</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={dataForSelected}
                        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="ts"
                          interval="preserveStartEnd"
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                          formatter={(v) => `${Number(v).toFixed(1)} °C`}
                          labelFormatter={(l) => `Ore ${l}`}
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          dot={false}
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              className="fixed inset-0 bg-black/40 z-50 flex"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
            >
              <motion.div
                className="ml-auto h-full w-full max-w-xl bg-white shadow-xl p-6 overflow-y-auto"
                initial={{ x: 40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 40, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Strumenti</h3>
                  <button
                    className="text-slate-600 hover:opacity-80"
                    onClick={() => setMenuOpen(false)}
                  >
                    Chiudi
                  </button>
                </div>

                <div className="space-y-7">
                  <ToolSection title="🚨 EMERGENZA E CONTROLLO">
                    <div className="col-span-2 md:col-span-3">
                      <ToolCard icon={<AlertTriangle />} label="PANIC MODE" href="/panic" requiredPerm={["PANIC_REPORT", "PANIC_MODE"]} hasPerm={hasAnyPerm} onLocked={openUpgradeModal} danger />
                    </div>
                    <ToolCard icon={<BookOpen />} label="Manuale HACCP" href={hasPerm("HACCP_MANUALE") ? "/manuale" : "/api/documenti_azienda.php?cat=manuale_haccp"} />
                    <ToolCard icon={<Brain />} label="Norma AI" href="/norma" requiredPerm="NORMA_AI" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<ClipboardCheck />} label="Audit di Controllo" href="/audit-haccp" requiredPerm="AUDIT_CONTROLLO" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<FileText />} label="Verbali Organi di Controllo" href="/api/documenti_azienda.php?cat=verbali_controllo" requiredPerm="VERBALI_ORGANI" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<ClipboardList />} label="Audit di Controllo Esterno" href="/api/documenti_azienda.php?cat=audit_controllo_esterno" requiredPerm="AUDIT_CONTROLLO" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                  </ToolSection>

                  <ToolSection title="🎓 FORMAZIONE E QUALIFICHE">
                    <ToolCard icon={<GraduationCap />} label="Norma Academy" href="/richiedi-corso" disabled={!['admin', 'superadmin'].includes(userInfo?.ruolo) && !userInfo?.is_super} />
                    <ToolCard icon={<GraduationCap />} label="Certificati e Attestati" href="/api/documenti_azienda.php?cat=corso_alimentaristi" requiredPerm="ALIMENTARISTI" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<Beaker />} label="Analisi Laboratorio" href="/api/documenti_azienda.php?cat=analisi_laboratorio" requiredPerm="ANALISI_LAB" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                  </ToolSection>

                  <ToolSection title="⚙️ OPERATIVITÀ QUOTIDIANA">
                    <ToolCard icon={<PackageCheck />} label="Ricevimento Merci" href="/ricevimento" requiredPerm="RICEVIMENTO_MERCI" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<Flame />} label="Temperature Cottura & Abbattimento" href="/abbattimenti" />
                    <ToolCard icon={<Beaker />} label="Registro Abbattimenti" href="/abbattimenti/registro" />
                    <ToolCard icon={<ShieldCheck />} label="Piano di Sanificazione" href="/sanificazione" requiredPerm="PIANO_SANIF" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<ClipboardCheck />} label="Igiene Personale" href="/igiene-personale" requiredPerm="IGIENE_PERSONALE" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<Tag />} label="Etichette" href="/etichette" requiredPerm="ETICHETTE" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<AlertTriangle />} label="Non Conformità" href="/non-conformita" requiredPerm="NON_CONFORMITA" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<Flame />} label="Verifica Oli Frittura" href="/verifica-olii-frittura" requiredPerm="OLII_FRITTURA" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<Bug />} label="Monitoraggio Infestanti" href={infestantiHref} requiredPerm="INFESTANTI" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<Wrench />} label="Manutenzione" href="/manutenzioni" requiredPerm="MANUTENZIONE" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<FileText />} label="Procedure" href="/procedure" requiredPerm="PROCEDURE" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                  </ToolSection>

                  <ToolSection title="📚 DOCUMENTAZIONE">
                    <ToolCard icon={<FileText />} label="Registri" href="/registri" requiredPerm="REGISTRI" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<FileText />} label="Allegati di Gestione" href="/allegati-gestione" requiredPerm="ALLEGATI_GEST" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                    <ToolCard icon={<FileText />} label="Archivio DDT" href="/archivio-ddt" requiredPerm="RICEVIMENTO_MERCI" hasPerm={hasPerm} onLocked={openUpgradeModal} />
                  </ToolSection>

                  {canAccessShop(userInfo) && (
                    <ToolSection title="🛒 SERVIZI">
                      <ToolCard icon={<ShoppingBag />} label="Shop" href="/shop" />
                    </ToolSection>
                  )}
                </div>

                <div className="hidden">
                  <ToolCard
                    icon={<BookOpen />}
                    label="Manuale HACCP"
                    href={
                      hasPerm("HACCP_MANUALE")
                        ? "/manuale"
                        : "/api/documenti_azienda.php?cat=manuale_haccp"
                    }
                  />
                  <ToolCard
                    icon={<GraduationCap />}
                    label="Certificati e Attestati"
                    href="/api/documenti_azienda.php?cat=corso_alimentaristi"
                    requiredPerm="ALIMENTARISTI"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<GraduationCap />}
                    label="Norma Accademy"
                    href="/richiedi-corso"
                    disabled={!['admin', 'superadmin'].includes(userInfo?.ruolo) && !userInfo?.is_super}
                  />
                  {canAccessShop(userInfo) && (
                    <ToolCard icon={<ShoppingBag />} label="Shop" href="/shop" />
                  )}
                  <ToolCard
                    icon={<Brain />}
                    label="Norma AI"
                    href="/norma"
                    requiredPerm="NORMA_AI"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<ClipboardCheck />}
                    label="Audit di Controllo"
                    href="/audit-haccp"
                    requiredPerm="AUDIT_CONTROLLO"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<ClipboardList />}
                    label="Audit di Controllo Esterno"
                    href="/api/documenti_azienda.php?cat=audit_controllo_esterno"
                    requiredPerm="AUDIT_CONTROLLO"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<FileText />}
                    label="Verbali Organi di Controllo"
                    href="/api/documenti_azienda.php?cat=verbali_controllo"
                    requiredPerm="VERBALI_ORGANI"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<Beaker />}
                    label="Analisi Laboratorio"
                    href="/api/documenti_azienda.php?cat=analisi_laboratorio"
                    requiredPerm="ANALISI_LAB"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<Flame />}
                    label="Temp. Cottura & Abbatt."
                    href="/abbattimenti"
                  />
                  <ToolCard
                    icon={<ShieldCheck />}
                    label="Piano di Sanificazione e Pulizia"
                    href="/sanificazione"
                    requiredPerm="PIANO_SANIF"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<ClipboardCheck />}
                    label="Igiene Personale"
                    href="/igiene-personale"
                    requiredPerm="IGIENE_PERSONALE"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<FileText />}
                    label="Allegati di Gestione"
                    href="/allegati-gestione"
                    requiredPerm="ALLEGATI_GEST"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<Tag />}
                    label="Etichette"
                    href="/etichette"
                    requiredPerm="ETICHETTE"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<PackageCheck />}
                    label="Ricevimento Merci"
                    href="/ricevimento"
                    requiredPerm="RICEVIMENTO_MERCI"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<Bug />}
                    label={infestantiLabel}
                    href={infestantiHref}
                    requiredPerm="INFESTANTI"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<Wrench />}
                    label="Manutenzione"
                    href="/manutenzioni"
                    requiredPerm="MANUTENZIONE"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<Beaker />}
                    label="Registro Abbattimenti"
                    href="/abbattimenti/registro"
                  />
                  <ToolCard
                    icon={<Flame />}
                    label="Verifica Olii Frittura"
                    href="/verifica-olii-frittura"
                    requiredPerm="OLII_FRITTURA"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<FileText />}
                    label="Procedure"
                    href="/procedure"
                    requiredPerm="PROCEDURE"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<AlertTriangle />}
                    label="Non Conformità"
                    href="/non-conformita"
                    requiredPerm="NON_CONFORMITA"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<FileText />}
                    label="Registri"
                    href="/registri"
                    requiredPerm="REGISTRI"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                  <ToolCard
                    icon={<AlertTriangle />}
                    label="PANIC MODE"
                    href="/panic"
                    requiredPerm={["PANIC_REPORT", "PANIC_MODE"]}
                    hasPerm={hasAnyPerm}
                    onLocked={openUpgradeModal}
                    danger
                  />
                  <ToolCard
                    icon={<FileText />}
                    label="ARCHIVIO DDT"
                    href="/archivio-ddt"
                    requiredPerm="RICEVIMENTO_MERCI"
                    hasPerm={hasPerm}
                    onLocked={openUpgradeModal}
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {hasPerm("NORMA_AI") && <NormaWidget />}

        {upgradeModal.open && (
          <TrialPopupModal
            popup={{
              key: "premium_feature",
              image_url: "/img/trial-popups/popup-4.png",
              feature_label: upgradeModal.featureLabel,
            }}
            onClose={closeUpgradeModal}
          />
        )}
      </div>

      <Footer />
    </>
  );
}

function Semaforo({ label, state }) {
  const { level, note } = state || { level: "grey", note: "—" };
  const icon =
    level === "green" ? (
      <CheckCircle className="shrink-0" size={18} />
    ) : level === "yellow" ? (
      <AlertTriangle className="shrink-0" size={18} />
    ) : (
      <XCircle className="shrink-0" size={18} />
    );

  const tone =
    level === "green"
      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
      : level === "yellow"
        ? "bg-amber-50 border-amber-200 text-amber-700"
        : "bg-rose-50 border-rose-200 text-rose-700";

  return (
    <div className={`flex items-start gap-3 rounded-xl p-3 border ${tone}`}>
      {icon}
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-sm opacity-90">{note}</div>
      </div>
    </div>
  );
}

function ToolSection({ title, subtitle, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
      <div className="mb-3">
        <h4 className="text-sm font-bold uppercase tracking-wide text-slate-800">{title}</h4>
        {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
      </div>
      <div className="grid grid-cols-2 items-stretch gap-3 md:grid-cols-3">{children}</div>
    </section>
  );
}

function ToolCard({
  icon,
  label,
  badge,
  href,
  onClick,
  requiredPerm,
  hasPerm,
  onLocked,
  disabled,
  danger = false,
}) {
  const enabled =
    !disabled &&
    (!requiredPerm || (typeof hasPerm === "function" && hasPerm(requiredPerm)));

  const handleClick = (e) => {
    e.preventDefault();
    if (!enabled) {
      if (!disabled) onLocked?.(label);
      return;
    }
    if (onClick) onClick();
    else if (href) window.location.href = href;
  };

  const common = [
    "flex h-full min-h-[76px] w-full items-start gap-3 p-3 rounded-xl border text-left transition",
    enabled
      ? danger
        ? "bg-red-600 text-white border-red-700 hover:bg-red-700 cursor-pointer shadow-sm"
        : "bg-white border-slate-200 hover:bg-slate-50 cursor-pointer"
      : "opacity-60 border-dashed cursor-not-allowed bg-white",
  ].join(" ");

  const iconBox = danger
    ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/15 border border-white/20 [&>svg]:h-5 [&>svg]:w-5"
    : "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 [&>svg]:h-5 [&>svg]:w-5";

  return (
    <button type="button" onClick={handleClick} className={common}>
      <div className={iconBox}>{icon}</div>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className={`font-medium leading-5 break-words ${danger ? "text-white" : ""}`}>{label}</div>
        {badge ? (
          <div className="mt-1 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
            {badge}
          </div>
        ) : null}
      </div>
    </button>
  );
}
